/* eslint-disable react-hooks/exhaustive-deps */
import Button from 'components/Form/Button';
import { Icon, Icons } from 'components/Icon';
import Breadcrumb from 'components/Navigation/Breadcrumb';
import React, { useCallback, useEffect, useState } from 'react';
import Badge from 'components/Badge';
import TableProperties from 'components/TableWidget/TableProperties';
import AddGuardianModal from './AddGuardianModal';
import TableActionWidget from 'components/TableWidget/TableActionWidget';
import {
  useDeleteGuardian,
  useGetGuardianList
} from 'common/queries-and-mutations/guardian';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { Loader } from 'components/Loader';
import { GUARDIAN_TABLE_REPORT } from 'constants/download-table-header';
import EditGuardianModal from './EditGuardianModal';
import ConfirmModal from 'components/Modal/ConfirmModal';
import withCreatePortal from 'components/HOC/withCreatePortal';

const EnhancedAddGuardianForm = withCreatePortal(AddGuardianModal);
const EnhancedEditGuardianForm = withCreatePortal(EditGuardianModal);
const EnhancedConfirmModal = withCreatePortal(ConfirmModal);

export default function Guardians() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedId, setSelectedId] = useState<null | number>(null);
  const [userId, setUserId] = useState<null | number>(null);
  const [isExecutingSearch, setIsExecutingSearch] = useState<boolean>(true);
  const [showAskToConfirm, setShowAskToConfirm] = useState(false);

  const {
    mutate: deleteGuardian,
    isSuccess: isDeleted,
    isLoading: isDeleting
  } = useDeleteGuardian();

  const { data, isLoading, refetch } = useGetGuardianList({
    enabled: isExecutingSearch,
    search: searchQuery
  });

  useEffect(() => {
    if (isDeleted) {
      refetch();
      setUserId(null);
      setShowAskToConfirm(false);
    }
  }, [isDeleting, isDeleted]);

  const executeSearch = useCallback(
    debounce(() => {
      setIsExecutingSearch(true);
    }, 2000),
    []
  );

  const handleChange = (val: any) => {
    setIsExecutingSearch(false);
    if (val === '') {
      setSearchQuery('');
      executeSearch();
    }
    if (val.trim()) {
      setSearchQuery(val);
      executeSearch();
    }
  };

  const onYesClick = () => {
    deleteGuardian(userId!);
  };

  return (
    <div>
      {isLoading ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3">
            <div className="col-span-2 col-start-1">
              <Breadcrumb title="Guardian Management" />
            </div>
            <div className="col-span-1 col-start-7 col-end-7">
              <Button
                variant="primary"
                size="sm"
                className="mt-1 w-[10rem] rounded border-none px-[0.5rem] text-xs"
                label="Add Guardian"
                onClick={() => setShowAddModal(true)}
              ></Button>
            </div>
          </div>

          <TableProperties
            showTab={false}
            searchQuery={searchQuery}
            handleSearch={val => handleChange(val)}
            exportData={data}
            exportHeader={GUARDIAN_TABLE_REPORT}
            fileName="Guardian"
            isLoading={isLoading}
          />

          <div className="relative mt-2 min-h-[40rem] overflow-x-auto bg-white sm:rounded-lg">
            <table className="table-to-print w-full px-6 py-4 text-left text-sm">
              <thead className="border-b bg-white text-xs text-[#1F4173]">
                <tr>
                  <th scope="col" className="px-6 py-4">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-4">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-4">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-4">
                    Status
                  </th>
                  <th scope="col" className="column-to-hide px-6 py-4">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="px-6 py-4">
                {!data || data.length < 1 ? (
                  <tr className="text-center">
                    <td colSpan={5} className="py-10 text-lg">
                      No results found
                    </td>
                  </tr>
                ) : (
                  data?.map(guardian => (
                    <tr
                      key={guardian.id}
                      className="border-b bg-white hover:bg-gray-50"
                    >
                      <td className="px-6 py-4  text-xs font-normal text-[#353535]">
                        {`${guardian.guardianFirstName} ${guardian.guardianMiddleName} ${guardian.guardianLastName}`}
                      </td>
                      <td className="px-6 py-4  text-xs font-normal text-[#353535]">
                        {guardian.emailAddress}
                      </td>
                      <td className="px-6 py-4  text-xs font-normal text-[#353535]">
                        {guardian.guardianType} Guardian
                      </td>
                      <td className="px-6 py-4 ">
                        <Badge
                          title={
                            guardian.isAssigned ? 'Assigned' : 'Unassigned'
                          }
                          type={guardian.isAssigned ? 'success' : 'danger'}
                          size="sm"
                        />
                      </td>
                      <td className="column-to-hide px-6 py-4">
                        <button
                          type="button"
                          role="button"
                          tabIndex={-1}
                          onKeyDown={() => {
                            setSelectedId(guardian.id);
                          }}
                          onClick={() => {
                            setSelectedId(guardian.id);
                          }}
                        >
                          <Icon name={Icons.MoreMenu} />
                        </button>
                        {selectedId === guardian.id && isDeleting && (
                          <Loader height={20} width={20} type={'Wait'} />
                        )}
                        {selectedId === guardian.id && (
                          <TableActionWidget
                            onViewClick={() =>
                              navigate(`profile/${guardian.id}`)
                            }
                            onEditClick={() => {
                              setShowEditModal(true);
                              setUserId(guardian.id);
                            }}
                            // onAssignClick={() =>
                            //   navigate(`profile/${guardian.id}`)
                            // }
                            onRemoveClick={() => {
                              setShowAskToConfirm(true);
                              setUserId(guardian.id);
                            }}
                            onDismiss={() => setSelectedId(null)}
                          />
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      {showAddModal && (
        <EnhancedAddGuardianForm
          onClose={() => {
            setShowAddModal(false);
          }}
          onSuccess={() => {
            setShowAddModal(false);
            refetch();
          }}
          disableType={false}
        />
      )}
      {showEditModal && userId && (
        <EnhancedEditGuardianForm
          onClose={() => {
            setShowEditModal(false);
            refetch();
          }}
          onSuccess={() => {
            refetch();
            setShowEditModal(false);
            setUserId(null);
          }}
          guardianId={userId}
        />
      )}
      {showAskToConfirm && userId && (
        <EnhancedConfirmModal
          content="Are you sure you want to remove this guardian?"
          onConfirm={onYesClick}
          onCancel={() => {
            setShowAskToConfirm(false);
            setUserId(null);
          }}
          actionText="Yes, Remove"
          cancelText="No"
          title="Action Required"
        />
      )}
    </div>
  );
}
